/**
 * ClientConfigPage — remote knobs for desktop clients (feature flags, batch sizes).
 */

import { useCallback, useEffect, useState } from "react";
import {
  getAdminClientConfig,
  updateAdminClientConfig,
  type ClientRemoteConfig,
} from "../api/adminClient";

const inputClass =
  "w-full px-4 py-2.5 border border-[var(--color-outline-variant)] rounded-lg text-sm bg-[var(--color-surface)] text-[var(--color-on-surface)] placeholder:text-[var(--color-outline)] focus:outline-none focus:border-[var(--color-accent-blue)] focus:ring-1 focus:ring-[var(--color-accent-blue)]";

const DEFAULTS: ClientRemoteConfig = {
  config_version: 1,
  feature_byok: false,
  feature_batch_pipeline: true,
  feature_clip_batch_mode: false,
  ocr_batch_size: 10,
  vectorize_text_chunk: 32,
  vectorize_fusion_chunk: 4,
  ocr_pipeline_max_retries: 3,
  ocr_pipeline_retry_backoff_secs: 2,
  pipeline_min_balance_cny: 0.5,
  fusion_mode_default: false,
  force_fusion_mode: false,
  min_client_version: null,
  notice_message: null,
};

interface Props {
  onAuthError?: () => void;
}

function BoolRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 mb-4 cursor-pointer">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1"
      />
      <span>
        <span className="block text-sm text-[var(--color-on-surface)]">{label}</span>
        {hint && (
          <span className="block text-xs text-[var(--color-on-surface-variant)] mt-0.5">
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}

function NumRow({
  label,
  hint,
  value,
  step,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  step?: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-medium text-[var(--color-on-surface-variant)] mb-1.5">
        {label}
      </label>
      <input
        type="number"
        step={step ?? "1"}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(Number(e.target.value))}
        className={inputClass}
      />
      {hint && (
        <p className="mt-1 text-xs text-[var(--color-on-surface-variant)]">{hint}</p>
      )}
    </div>
  );
}

export default function ClientConfigPage({ onAuthError }: Props) {
  const [local, setLocal] = useState<ClientRemoteConfig>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const cfg = await getAdminClientConfig();
      setLocal({ ...DEFAULTS, ...cfg });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/401|403|未授权|管理员/.test(msg)) {
        onAuthError?.();
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => {
    void load();
  }, [load]);

  const setField = <K extends keyof ClientRemoteConfig>(key: K, value: ClientRemoteConfig[K]) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const result = await updateAdminClientConfig({
        ...local,
        min_client_version: local.min_client_version?.trim() || null,
        notice_message: local.notice_message?.trim() || null,
      });
      setLocal({ ...DEFAULTS, ...result.config });
      setMessage(result.message || "已保存");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/401|403|未授权|管理员/.test(msg)) {
        onAuthError?.();
      }
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-[var(--color-on-surface-variant)]">加载客户端配置…</div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[var(--color-on-surface)]">客户端远程配置</h2>
        <p className="mt-1 text-sm text-[var(--color-on-surface-variant)]">
          修改后客户端下次拉取即生效，无需重编或重装桌面端。本机项（数据库端口、路径映射）仍由用户本地配置。
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {message && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {message}
        </div>
      )}

      <section className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-6">
        <h3 className="text-sm font-semibold mb-4">功能开关</h3>
        <BoolRow
          label="FEATURE_BYOK"
          hint="允许自带 Key 模式入口"
          value={local.feature_byok}
          onChange={(v) => setField("feature_byok", v)}
        />
        <BoolRow
          label="FEATURE_BATCH_PIPELINE"
          hint="会员批处理流水线"
          value={local.feature_batch_pipeline}
          onChange={(v) => setField("feature_batch_pipeline", v)}
        />
        <BoolRow
          label="FEATURE_CLIP_BATCH_MODE"
          hint="CLIP 批量提交模式"
          value={local.feature_clip_batch_mode}
          onChange={(v) => setField("feature_clip_batch_mode", v)}
        />
        <BoolRow
          label="fusion_mode_default"
          hint="融合向量默认值（新偏好 / 未设置时）"
          value={local.fusion_mode_default}
          onChange={(v) => setField("fusion_mode_default", v)}
        />
        <BoolRow
          label="force_fusion_mode"
          hint="强制使用 fusion_mode_default，忽略用户本地偏好"
          value={local.force_fusion_mode}
          onChange={(v) => setField("force_fusion_mode", v)}
        />
      </section>

      <section className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-6">
        <h3 className="text-sm font-semibold mb-4">批量与重试</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <NumRow
            label="ocr_batch_size"
            hint="OCR 批大小 (1–200)"
            value={local.ocr_batch_size}
            onChange={(v) => setField("ocr_batch_size", v)}
          />
          <NumRow
            label="vectorize_text_chunk"
            hint="文本向量批大小 (1–256)"
            value={local.vectorize_text_chunk}
            onChange={(v) => setField("vectorize_text_chunk", v)}
          />
          <NumRow
            label="vectorize_fusion_chunk"
            hint="融合向量并发 (1–64)"
            value={local.vectorize_fusion_chunk}
            onChange={(v) => setField("vectorize_fusion_chunk", v)}
          />
          <NumRow
            label="ocr_pipeline_max_retries"
            value={local.ocr_pipeline_max_retries}
            onChange={(v) => setField("ocr_pipeline_max_retries", v)}
          />
          <NumRow
            label="ocr_pipeline_retry_backoff_secs"
            step="0.1"
            value={local.ocr_pipeline_retry_backoff_secs}
            onChange={(v) => setField("ocr_pipeline_retry_backoff_secs", v)}
          />
          <NumRow
            label="pipeline_min_balance_cny"
            step="0.01"
            hint="流水线最低余额门槛"
            value={local.pipeline_min_balance_cny}
            onChange={(v) => setField("pipeline_min_balance_cny", v)}
          />
        </div>
      </section>

      <section className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-6">
        <h3 className="text-sm font-semibold mb-4">提示</h3>
        <div className="mb-4">
          <label className="block text-xs font-medium text-[var(--color-on-surface-variant)] mb-1.5">
            min_client_version
          </label>
          <input
            type="text"
            value={local.min_client_version ?? ""}
            onChange={(e) => setField("min_client_version", e.target.value || null)}
            placeholder="例如 1.2.0（可选）"
            className={inputClass}
          />
        </div>
        <p className="text-xs text-[var(--color-on-surface-variant)] leading-relaxed">
          用户可见公告请到顶部导航「公告管理」维护（支持当前/历史、发布与下线）。此处不再编辑
          notice_message，避免与全局公告双入口。
        </p>
      </section>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="px-5 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-accent-blue)] text-white disabled:opacity-60"
        >
          {saving ? "保存中…" : "保存客户端配置"}
        </button>
        <button
          type="button"
          onClick={() => void load()}
          className="px-5 py-2.5 rounded-lg text-sm border border-[var(--color-outline-variant)]"
        >
          重新加载
        </button>
      </div>
    </div>
  );
}
